<!-- The ledger table: shared by the Transactions screen and a report's coding view (SD-1227). -->
<!-- Solid on the sorted column and pointing the way it sorts; a faint outline on the
     rest, so it reads as "you can sort by this" without competing with the header. -->
<ng-template #sortCaret let-column>
  <svg class="tx-th-caret" [class.tx-th-caret--up]="isSortedBy(column) && sortDir === 'asc'"
       viewBox="0 0 10 6" fill="none" aria-hidden="true">
    <path d="M1 1.5L5 5l4-3.5" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round" />
  </svg>
</ng-template>

<!-- SD-1254: the Coding Assistant's suggestions on the loaded rows: accept them all, or hide them.
     Hidden, it stays as one line, so they can be brought back from where they were put away. -->
<div class="tx-suggest-strip" *ngIf="!loading && pendingSuggestions.length"
     [class.tx-suggest-strip--off]="!suggestedCoding.isVisible">
  <svg class="tx-suggest-strip__icon" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M6 1.6l1.1 2.9L10 5.6 7.1 6.7 6 9.6 4.9 6.7 2 5.6l2.9-1.1L6 1.6z" fill="currentColor" />
  </svg>
  <span class="tx-suggest-strip__text" *ngIf="suggestedCoding.isVisible">
    The Coding Assistant suggested coding for
    <strong>{{ pendingSuggestions.length }} {{ pendingSuggestions.length === 1 ? 'transaction' : 'transactions' }}</strong>.
    <ng-container *ngIf="pendingSuggestions.length - acceptableSuggestions.length as review; else allReady">
      {{ review }} {{ review === 1 ? 'needs its' : 'need their' }} supplier checked or added: review {{ review === 1 ? 'it' : 'them' }}.
    </ng-container>
    <ng-template #allReady>Check each one, then accept it.</ng-template>
  </span>
  <span class="tx-suggest-strip__text" *ngIf="!suggestedCoding.isVisible">
    {{ pendingSuggestions.length }} suggested {{ pendingSuggestions.length === 1 ? 'coding' : 'codings' }} hidden
  </span>
  <span class="tx-suggest-strip__actions">
    <button type="button" class="tx-suggest-strip__accept" *ngIf="suggestedCoding.isVisible && acceptableSuggestions.length"
            (click)="acceptAllSuggestions()"
            [title]="'Accept the ' + acceptableSuggestions.length + ' whose supplier is matched'">
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2.5 6.2l2.2 2.2 4.8-5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      {{ acceptableSuggestions.length === pendingSuggestions.length ? 'Accept all' : 'Accept ' + acceptableSuggestions.length }}
    </button>
    <button type="button" class="tx-suggest-strip__toggle"
            (click)="suggestedCoding.setVisible(!suggestedCoding.isVisible)">
      {{ suggestedCoding.isVisible ? 'Hide suggestions' : 'Show suggestions' }}
    </button>
  </span>
</div>

<!-- SD-1235: framed and typed as Automate > Workflows' table (shared/styles/_list-table.scss). -->
<div class="tx-table-wrap sd-list-card" (scroll)="onTableScroll($event)">
  <table class="tx-table sd-list" [class.tx-table--narrow]="!showReportColumn">
    <thead>
      <tr>
        <th class="tx-col-check" *ngIf="!readOnly">
          <!-- SD-1235: a label round the box, so the tap target is 32px, not the box's 13. -->
          <label class="tx-check-hit">
            <input type="checkbox" [checked]="allSelected" (change)="toggleSelectAll()"
                   title="Select all loaded transactions" />
          </label>
        </th>
        <!-- Reconciled or not leads the row: it is what the filter pills above split
             the list by, and what decides whether the row needs you at all. -->
        <th appColumnResize class="tx-col-status" *ngIf="shows('status')">Status</th>

        <!--
          Only the four columns the server can order on are sortable. "Coded to" and
          "Report" are filled in by the API after the page has been cut, so sorting on
          them would order the hundred rows on screen and leave the rest of the
          statement where it was.
        -->
        <th appColumnResize class="tx-col-date tx-th--sortable" *ngIf="showDateColumn" [class.tx-th--on]="isSortedBy('date')"
            [attr.aria-sort]="ariaSort('date')" (click)="sortByColumn('date')">
          <span class="tx-th-label">Date <ng-container *ngTemplateOutlet="sortCaret; context: { $implicit: 'date' }"></ng-container></span>
        </th>

        <th appColumnResize class="tx-col-txn tx-th--sortable" *ngIf="shows('txn')" [class.tx-th--on]="isSortedBy('txn')"
            [attr.aria-sort]="ariaSort('txn')" (click)="sortByColumn('txn')"
            title="Sorts on the bank's own narration">
          <span class="tx-th-label">Transaction <ng-container *ngTemplateOutlet="sortCaret; context: { $implicit: 'txn' }"></ng-container></span>
        </th>

        <th appColumnResize class="tx-col-coded" *ngIf="shows('coded')">Coded to</th>
        <th appColumnResize class="tx-col-details" *ngIf="shows('details')">Details</th>
        <!-- Not sortable, for the same reason as "Coded to": filled in after the page is cut. -->
        <th appColumnResize class="tx-col-assigned" *ngIf="assignedVisible">Assigned to</th>

        <th appColumnResize class="tx-col-amount tx-th--sortable" *ngIf="shows('amount')" [class.tx-th--on]="isSortedBy('amount')"
            [attr.aria-sort]="ariaSort('amount')" (click)="sortByColumn('amount')">
          <span class="tx-th-label">Amount <ng-container *ngTemplateOutlet="sortCaret; context: { $implicit: 'amount' }"></ng-container></span>
        </th>

        <!-- Balance is each account's own running total. Across several accounts it reads per
             row but orders nothing, so it is only sortable on a single account's statement. -->
        <th appColumnResize class="tx-col-balance" *ngIf="balanceVisible"
            [class.tx-th--sortable]="!showAccountColumn"
            [class.tx-th--on]="isSortedBy('balance')"
            [attr.aria-sort]="ariaSort('balance')"
            (click)="!showAccountColumn && sortByColumn('balance')">
          <span class="tx-th-label">Balance <ng-container *ngTemplateOutlet="sortCaret; context: { $implicit: 'balance' }"></ng-container></span>
        </th>

        <th appColumnResize class="tx-col-account tx-th--sortable" *ngIf="accountVisible"
            [class.tx-th--on]="isSortedBy('account')"
            [attr.aria-sort]="ariaSort('account')" (click)="sortByColumn('account')">
          <span class="tx-th-label">Account <ng-container *ngTemplateOutlet="sortCaret; context: { $implicit: 'account' }"></ng-container></span>
        </th>

        <!-- Off inside a report: every row would name the report you are already in. -->
        <th appColumnResize class="tx-col-report" *ngIf="reportVisible">Report</th>
        <th appColumnResize class="tx-col-source" *ngIf="sourceVisible">Source</th>
      </tr>
    </thead>

    <tbody *ngFor="let group of groups; trackBy: trackByGroup">
      <!-- With the Date column gone the day headers are the date sort: click one to flip
           between oldest and newest first. -->
      <tr class="tx-day" *ngIf="showDayHeaders" tabindex="0"
          [title]="sortDir === 'asc' ? 'Oldest first. Click for newest first' : 'Newest first. Click for oldest first'"
          (click)="sortByColumn('date')" (keydown.enter)="sortByColumn('date')">
        <td [attr.colspan]="shows('amount') ? daySpanBeforeAmount : columnCount">
          <span class="tx-day-label">{{ group.label }}
            <svg class="tx-day-caret" [class.tx-th-caret--up]="sortDir === 'asc'"
                 viewBox="0 0 10 6" fill="none" aria-hidden="true">
              <path d="M1 1.5L5 5l4-3.5" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </td>
        <!-- In the Amount column, over the figures it adds up. -->
        <td class="tx-col-amount tx-day-net" *ngIf="shows('amount')">Net {{ signed(group.net) }}</td>
        <td *ngIf="shows('amount') && daySpanAfterAmount" [attr.colspan]="daySpanAfterAmount"></td>
      </tr>

      <tr class="tx-row" *ngFor="let row of group.rows; trackBy: trackByRow"
          [class.tx-row--on]="isSelected(row)" [class.tx-row--working]="isRowCoding(row)"
          (click)="onRowClick(row)">

        <td class="tx-col-check" *ngIf="!readOnly" (click)="toggleSelect(row, $event)">
          <input type="checkbox" [checked]="isSelected(row)" (click)="$event.stopPropagation()"
                 (change)="toggleSelect(row)" />
        </td>

        <td class="tx-col-status" *ngIf="shows('status')">
          <span class="tx-chip tx-chip--working" *ngIf="isRowCoding(row)" title="Coding this transaction">
            <span class="tx-chip-spinner" aria-hidden="true"></span>
            Coding…
          </span>

          <!-- A tick or an open ring, so the two states differ in shape as well as in colour
               and can be told apart at a glance, or without colour at all. -->
          <span class="tx-chip" *ngIf="!isRowCoding(row)" [ngClass]="statusClass(row)"                [title]="statusTitle(row)">
            <svg class="tx-chip-icon" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path *ngIf="isReconciledRow(row)" d="M2.4 6.3l2.4 2.4 4.8-5" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <circle *ngIf="!isReconciledRow(row)" cx="6" cy="6" r="3.1" stroke="currentColor"
                      stroke-width="2" />
            </svg>
            {{ statusLabel(row) }}
          </span>
        </td>

        <td class="tx-col-date" *ngIf="showDateColumn">{{ displayDate(row.transactionDate) }}</td>

        <td class="tx-col-txn" *ngIf="shows('txn')">
          <div class="tx-txn">
            <!--
              Two identity states. A matched row has a real supplier from the linked
              invoice: its avatar, its name, and the narration beneath. An unmatched one
              has only the bank's narration, shown on its own rather than dressed up as
              a name we do not have.
            -->
            <app-supplier-avatar class="tx-avatar" *ngIf="tileSupplierName(row)"
                                 [class.tx-avatar--new]="newSupplierName(row)"
                                 [supplierId]="tileSupplierId(row)"
                                 [supplierName]="tileLogoName(row)">{{ initials(row) }}</app-supplier-avatar>

            <span class="tx-txn-main">
              <span class="tx-txn-title">
                {{ rowTitle(row) }}
                <!-- SD-1254: the Coding Assistant's supplier, until a person confirms it, and how sure. -->
                <ng-container [ngSwitch]="supplierMatch(row)">
                  <span class="tx-suggested-tag" *ngSwitchCase="'matched'"
                        title="Suggested by the Coding Assistant">Suggested</span>
                  <span class="tx-suggested-tag tx-suggested-tag--check" *ngSwitchCase="'possible'"
                        title="The transaction does not name this supplier: the Coding Assistant's closest guess">Check supplier</span>
                  <span class="tx-suggested-tag tx-suggested-tag--new" *ngSwitchCase="'new'"
                        title="None of your suppliers matched: add this one when you code it">New supplier</span>
                  <span class="tx-suggested-tag tx-suggested-tag--none" *ngSwitchCase="'none'"
                        title="The Coding Assistant suggested an account but no supplier">No supplier matched</span>
                </ng-container>
              </span>
              <span class="tx-txn-sub" *ngIf="rowSubtitle(row)">{{ rowSubtitle(row) }}</span>
            </span>
          </div>
        </td>

        <!-- Account over tax, from the invoice the transaction is matched or coded to. -->
        <td class="tx-col-coded" *ngIf="shows('coded')">
          <div class="tx-coded" *ngIf="row.codedAccountName; else notCoded" [title]="codedLabel(row)">
            <span class="tx-coded-main">
              <span class="tx-coded-account">{{ codedLabel(row) }}</span>
              <span class="tx-coded-tax" *ngIf="row.codedTaxName">{{ row.codedTaxName }}</span>
            </span>
          </div>
          <ng-template #notCoded>
            <!-- A suggestion is a lead, so it is always on screen rather than waiting for a
                 hover, and it opens the panel straight at the match to accept or reject. -->
            <span class="tx-coding-note" *ngIf="isRowCoding(row)">Applying coding…</span>

            <button type="button" class="tx-match-btn"
                    *ngIf="!readOnly && !isRowCoding(row) && !isReconciledRow(row) && row.hasSuggestedMatch"
                    (click)="onReviewMatch(row, $event)" title="A matching invoice was found">
              <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1.6l1.1 2.9L10 5.6 7.1 6.7 6 9.6 4.9 6.7 2 5.6l2.9-1.1L6 1.6z" fill="currentColor" />
              </svg>
              Match found
            </button>

            <!-- SD-1254: the Coding Assistant's suggested coding. A suggestion, not coding yet: the lead's
                 sparkle and Match found's blue, not a tick. It opens the coding dialog to change it;
                 Accept takes it as it is (and posts it). -->
            <span class="tx-agent-coding" *ngIf="showsSuggestion(row)">
              <span class="tx-agent-coding__line">
                <button type="button" class="tx-code-btn tx-code-btn--agent"
                        (click)="onCodeRow(row, $event)" [title]="agentCodingTitle(row) + ' Click to change it, or Accept to use it as it is.'">
                  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M6 1.6l1.1 2.9L10 5.6 7.1 6.7 6 9.6 4.9 6.7 2 5.6l2.9-1.1L6 1.6z" fill="currentColor" />
                  </svg>
                  <span class="tx-code-btn__label">Suggested: {{ agentCodingLabel(row) }}</span>
                </button>
                <button type="button" class="tx-accept-btn" *ngIf="canAccept(row)" (click)="onAcceptSuggestion(row, $event)"
                        title="Accept this coding" [attr.aria-label]="'Accept ' + agentCodingLabel(row)">
                  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2.5 6.2l2.2 2.2 4.8-5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Accept
                </button>
                <!-- The supplier is a guess, new or missing: coded in the form, where it is checked or added. -->
                <button type="button" class="tx-review-btn" *ngIf="!canAccept(row)" (click)="onCodeRow(row, $event)"
                        title="Check or add the supplier, then code it">Review</button>
              </span>
              <span class="tx-agent-coding__note" *ngIf="agentCodingNote(row)">{{ agentCodingNote(row) }}</span>
            </span>

            <!-- Nothing suggested: the cell that is empty is the one you would fill. -->
            <button type="button" class="tx-code-btn"
                    *ngIf="!readOnly && !isRowCoding(row) && !isReconciledRow(row) && !row.hasSuggestedMatch && !showsSuggestion(row)"
                    (click)="onCodeRow(row, $event)">
              <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 2v8M2 6h8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
              Add coding
            </button>

            <!-- Reconciled, but not through Synced: the coding lives in the accounting system, so
                 say where rather than leave a dash that reads as missing. -->
            <span class="tx-elsewhere" *ngIf="!isRowCoding(row) && isReconciledRow(row)"
                  title="Reconciled in your accounting system. Synced doesn't have its coding.">
              Reconciled outside Synced
            </span>
            <span class="tx-dash" *ngIf="!isRowCoding(row) && !isReconciledRow(row) && readOnly">&mdash;</span>
          </ng-template>
        </td>

        <!-- What was typed when the row was coded. Truncated with the full text on hover:
             it is a note, not a field anyone reads a column of in full. -->
        <td class="tx-col-details" *ngIf="shows('details')">
          <div class="tx-details-cell">
            <span class="tx-details" *ngIf="row.codedDetails" [title]="row.codedDetails">
              {{ row.codedDetails }}
            </span>
            <span class="tx-dash" *ngIf="!row.codedDetails">&mdash;</span>

            <!-- Report view: the matched document's file, and the conversation on the row. -->
            <span class="tx-row-meta" *ngIf="showAttachment || showComments">
              <button type="button" class="tx-meta-btn" *ngIf="showAttachment && row.attachmentUrl"
                      (click)="onAttachment(row, $event)" title="View the attached document"
                      aria-label="View the attached document">
                <i class="fa fa-paperclip" aria-hidden="true"></i>
              </button>
              <button type="button" class="tx-meta-btn" *ngIf="showComments"
                      [class.tx-meta-btn--on]="row.commentCount > 0"
                      (click)="onComments(row, $event)"
                      [title]="row.commentCount ? row.commentCount + ' comments' : 'Add a comment'"
                      [attr.aria-label]="row.commentCount ? row.commentCount + ' comments' : 'Add a comment'">
                <i class="fa fa-comment-o" aria-hidden="true"></i>
                <span *ngIf="row.commentCount > 0">{{ row.commentCount }}</span>
              </button>
            </span>
          </div>
        </td>

        <td class="tx-col-assigned" *ngIf="assignedVisible">
          <span *ngIf="row.assignedToName" class="tx-assigned-name" [title]="row.assignedToName">
            {{ row.assignedToName }}
          </span>
          <span *ngIf="!row.assignedToName" class="tx-dash">&mdash;</span>
        </td>

        <td class="tx-col-amount" *ngIf="shows('amount')">{{ signed(row.amount) }}</td>

        <td class="tx-col-balance" *ngIf="balanceVisible"
            title="Running total from the first imported transaction on this account">
          {{ money(row.runningBalance) }}
        </td>

        <td class="tx-col-account" *ngIf="accountVisible">
          <span class="tx-account-name" [title]="row.accountName">{{ row.accountName }}</span>
        </td>

        <td class="tx-col-report" *ngIf="reportVisible">
          <span *ngIf="row.reportID" class="tx-report-name" [title]="row.reportName || 'In a report'">
            {{ row.reportName || 'In a report' }}
          </span>
          <span *ngIf="!row.reportID" class="tx-dash">&mdash;</span>
        </td>

        <!-- Where the row came from: the bank feed, Xero, a spreadsheet... -->
        <td class="tx-col-source" *ngIf="sourceVisible">
          <span *ngIf="sourceLabel(row)" class="tx-source-name" [title]="row.importMethod">{{ sourceLabel(row) }}</span>
          <span *ngIf="!sourceLabel(row)" class="tx-dash">&mdash;</span>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- SD-1235: skeleton rows under the real header, as on Workflows, rather than a line of text. -->
  <app-list-skeleton *ngIf="loading && !rows?.length" class="tx-skeleton" [class.tx-skeleton--narrow]="!showReportColumn"
    [framed]="false" [rows]="10" [columns]="skeletonColumns"></app-list-skeleton>
  <div class="tx-empty" *ngIf="loading && rows?.length">Loading transactions&hellip;</div>

  <div class="tx-empty" *ngIf="!loading && !rows.length">{{ emptyMessage }}</div>

  <div class="tx-empty" *ngIf="loadingMore">Loading more&hellip;</div>
</div>
